import { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import { ButtonBase } from '@mui/material';
import { FullscreenMediaFrame } from './FullscreenMediaFrame';
import { MediaPreview } from './MediaPreview';
import { CenterIcon } from './CenterIcon';
import { VideoDuration } from './VideoDuration';
import { MediaCardRemoveButton } from './MediaCardRemoveButton';
import { Timestamp } from './TimeStamp';

export enum PreviewSize {
  Small = 'small',
  Large = 'large',
}

export enum MediaType {
  Image = 'image',
  Video = 'video',
}

interface WrapperProps {
  $height?: number;
  $borderColor?: 'primary' | 'secondary';
}

const Wrapper = styled(ButtonBase)<WrapperProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.6s filter ease;
  border-radius: inherit;
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  width: 100%;
  height: 100%;
`;

type Props = {
  size: PreviewSize;
  mediaType: MediaType;
  url: string;
  timestamp?: string;
  borderColor?: 'primary' | 'secondary';
  sending?: boolean;
  showCenterIcon?: boolean;
  showVideoDuration?: boolean;
  handleRemove?: (url: string) => void;
};

export const MediaCard: FC<Props> = ({
  url,
  timestamp,
  mediaType,
  borderColor,
  sending,
  showCenterIcon,
  showVideoDuration,
  handleRemove,
  size,
}) => {
  const [isMediaLoaded, setIsMediaLoaded] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const handleMediaClick = () => setIsFullScreen(true);
  const handleMediaLoaded = () => setIsMediaLoaded(true);

  const videoElementRef = useRef<HTMLVideoElement>(null);
  const [videoDuration, setVideoDuration] = useState<number>();

  return (
    <>
      <Wrapper onClick={handleMediaClick} $borderColor={borderColor}>
        <MediaPreview
          url={url}
          mediaType={mediaType}
          onLoadedData={handleMediaLoaded}
          onLoadedMetadata={() =>
            setVideoDuration(videoElementRef.current?.duration)
          }
          size={size}
          videoRef={videoElementRef}
          sending={Boolean(sending)}
        />
        {showCenterIcon && (
          <CenterIcon
            mediaType={mediaType}
            sending={Boolean(sending)}
            isMediaLoaded={isMediaLoaded}
          />
        )}
        {mediaType === 'video' && showVideoDuration && videoDuration && (
          <VideoDuration videoDuration={videoDuration} />
        )}
        {!!handleRemove && (
          <MediaCardRemoveButton
            onClick={() => {
              handleRemove(url);
            }}
          />
        )}
        {timestamp && <Timestamp timestamp={timestamp} />}
      </Wrapper>
      <FullscreenMediaFrame
        mediaType={mediaType}
        mediaUrl={url}
        isOpen={isFullScreen}
        onClose={() => setIsFullScreen(false)}
      />
    </>
  );
};
